
import Drawer from '~/components/reusables/drawer/Drawer.vue'
import { mapGetters, mapMutations } from 'vuex'
import queries from '~/mixins/queries'

export default {
  name: 'DrawerNda',
  components: { Drawer },
  mixins: [queries],
  data() {
    return {
      loading: false,
      formData: {},
      errors: {},
    }
  },
  computed: {
    ...mapGetters([
      'GET_DRAWER_NDA_OPEN',
      'GET_TRUST_CENTER_DB',
      'GET_CURRENT_DOC',
    ]),
    ndaContent() {
      return this.GET_TRUST_CENTER_DB.find(
        (i) => i.type === 'trustCenterSettings'
      )?.nda
    },

    ndaSettings() {
      const settings =
        this.GET_TRUST_CENTER_DB.find((i) => i.type === 'trustCenterSettings')
          ?.ndaSettings || {}

      // Filter out properties with false values
      const cleanedSettings = Object.fromEntries(
        Object.entries(settings).filter(([key, value]) => value !== false)
      )

      return cleanedSettings
    },
    formFields() {
      const fields = []
      for (const [key, value] of Object.entries(this.ndaSettings)) {
        if (
          key !== 'checkboxEnabled' &&
          key !== 'expirationValue' &&
          key !== 'ndaCheckboxText' &&
          key !== 'ndaCheckboxLinkUrl' &&
          key !== 'secondCheckboxEnabled' &&
          key !== 'secondNdaCheckboxText' &&
          key !== 'secondNdaCheckboxLinkUrl' &&
          typeof value !== 'number'
        ) {
          // Map 'name' to 'fullName' for API compatibility
          const fieldKey = key === 'name' ? 'fullName' : key
          fields.push({
            key: fieldKey,
            label: this.formatLabel(fieldKey),
          })
        }
      }
      // Define the desired field order
      const fieldOrder = ['fullName', 'jobTitle', 'email', 'companyName']
      // Sort fields according to the desired order
      return fields.sort((a, b) => {
        const indexA = fieldOrder.indexOf(a.key)
        const indexB = fieldOrder.indexOf(b.key)
        // If both fields are in the order array, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB
        }
        // If only one is in the order array, prioritize it
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        // If neither is in the order array, maintain original order
        return 0
      })
    },
    hasCheckbox() {
      return this.ndaSettings.checkboxEnabled === true
    },
    checkboxText() {
      return (
        this.ndaSettings.ndaCheckboxText ||
        'By checking this box, I confirm that I have read and agree to the NDA.'
      )
    },
    hasSecondCheckbox() {
      return (
        this.ndaSettings.secondCheckboxEnabled === true &&
        (this.ndaSettings.secondNdaCheckboxText || '').trim() !== ''
      )
    },
    secondCheckboxText() {
      return this.ndaSettings.secondNdaCheckboxText || ''
    },
    isFormValid() {
      // Check all form fields are filled
      for (const field of this.formFields) {
        const value = this.formData[field.key]
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return false
        }
        // Validate email format if it's an email field
        if (field.key === 'email' && !this.isValidEmail(value)) {
          return false
        }
      }

      // Check checkbox if it exists
      if (this.hasCheckbox && !this.formData.checkboxEnabled) {
        return false
      }

      // Check second checkbox if it exists (required when enabled)
      if (this.hasSecondCheckbox && !this.formData.secondCheckboxEnabled) {
        return false
      }

      return true
    },
  },
  mounted() {
    // Initialize form data
    this.initializeForm()

    //DONT DELETE THIS COMMENTED CODE - FOR DEV PURPOSES
    // Auto-open drawer for UI testing - remove this in production
    // this.SET_DRAWER_NDA_OPEN(true)
    // if (!this.GET_CURRENT_DOC) {
    //   this.$store.commit('nda/SET_CURRENT_DOC', {
    //     vdId: 'test-doc-id',
    //     vendorDocName: 'Test Document',
    //   })
    // }
  },
  methods: {
    ...mapMutations(['SET_DRAWER_NDA_OPEN']),
    initializeForm() {
      const newFormData = {}
      const newErrors = {}

      // Initialize form fields
      for (const field of this.formFields) {
        newFormData[field.key] = ''
        newErrors[field.key] = ''
      }

      // Initialize checkbox if it exists
      if (this.hasCheckbox) {
        newFormData.checkboxEnabled = false
        newErrors.checkboxEnabled = ''
      }

      // Initialize second checkbox if enabled (required, same as first)
      if (this.hasSecondCheckbox) {
        newFormData.secondCheckboxEnabled = false
        newErrors.secondCheckboxEnabled = ''
      }

      this.formData = newFormData
      this.errors = newErrors
    },
    handleClose() {
      this.resetForm()
      this.SET_DRAWER_NDA_OPEN(false)
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },
    clearErrorIfValid(fieldName) {
      const value = this.formData[fieldName]

      // If there's an error for this field, check if it's now valid
      if (this.errors[fieldName]) {
        if (fieldName === 'email') {
          // For email, clear error if it's now valid
          if (value && value.trim() !== '' && this.isValidEmail(value)) {
            this.errors[fieldName] = ''
          }
        } else {
          // For other fields, clear error if field has value
          if (value && value.trim() !== '') {
            this.errors[fieldName] = ''
          }
        }
      }
    },
    validateField(fieldName) {
      this.errors[fieldName] = ''
      const value = this.formData[fieldName]

      if (fieldName === 'checkboxEnabled') {
        if (!value) {
          this.errors.checkboxEnabled = 'You must approve the NDA to continue'
        }
      } else if (fieldName === 'secondCheckboxEnabled') {
        if (!value) {
          this.errors.secondCheckboxEnabled =
            'You must approve to continue'
        }
      } else {
        const field = this.formFields.find((f) => f.key === fieldName)
        if (field) {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            this.errors[fieldName] = `${field.label} is required`
          } else if (fieldName === 'email' && !this.isValidEmail(value)) {
            this.errors[fieldName] = 'Please enter a valid email address'
          }
        }
      }
    },
    formatLabel(key) {
      if (key === 'email') return 'Company Email'
      // Convert camelCase to Title Case
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim()
    },
    validateForm() {
      // Validate all form fields
      for (const field of this.formFields) {
        this.validateField(field.key)
      }

      // Validate checkbox if it exists
      if (this.hasCheckbox) {
        this.validateField('checkboxEnabled')
      }

      // Validate second checkbox if it exists
      if (this.hasSecondCheckbox) {
        this.validateField('secondCheckboxEnabled')
      }

      // Check if all validations passed
      const hasErrors = Object.values(this.errors).some((error) => error !== '')
      return !hasErrors && this.isFormValid
    },
    resetForm() {
      const newFormData = {}
      const newErrors = {}

      // Reset form fields
      for (const field of this.formFields) {
        newFormData[field.key] = ''
        newErrors[field.key] = ''
      }

      // Reset checkbox if it exists
      if (this.hasCheckbox) {
        newFormData.checkboxEnabled = false
        newErrors.checkboxEnabled = ''
      }

      // Reset second checkbox if enabled
      if (this.hasSecondCheckbox) {
        newFormData.secondCheckboxEnabled = false
        newErrors.secondCheckboxEnabled = ''
      }

      this.formData = newFormData
      this.errors = newErrors
    },
    async acceptNda() {
      if (!this.validateForm()) {
        return
      }

      this.loading = true
      try {
        // Build dynamic payload from formData
        const payload = {
          items: [
            {
              type: 'nda',
              vdId: this.GET_CURRENT_DOC.vdId,
            },
          ],
          ...this.formData,
        }

        await this.$axios.post('trust-center/document-access', payload)
        this.auditLogCreate({
          action: 'acceptNda',
          value: this.GET_CURRENT_DOC.vendorDocName,
          params: this.formData,
        })
        await this.fetchTrustCenterData()
        this.resetForm()
        this.SET_DRAWER_NDA_OPEN(false)
      } catch (error) {
        console.error('Error accepting NDA:', error)
      } finally {
        this.loading = false
      }
    },
  },
}
